import React from 'react';
import Image from 'next/legacy/image';
import cx from 'classnames';

import { ILink } from '../../../../../../types/contentful';

import { NavItem } from '../../../shared';
import Link from '../../../../Link';

import icChevronDown from '@/images/ic_chevron_down.svg';
import { IsNewBadge } from '~/components/Navbar/isNewBadge';

interface Props extends NavItem {
  titleExpanded: string;
  setTitleExpanded: (title: string | undefined) => void;
}
export function ModalNavCategory({
  name,
  title,
  url,
  links,
  tabletLinks,
  mobileLinks,
  titleExpanded,
  setTitleExpanded
}: Props) {
  const expanded = name === titleExpanded;
  const isMeganav = tabletLinks && mobileLinks;
  const ifLinks = links || tabletLinks || mobileLinks;

  const toggleCategoryDisplay = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (titleExpanded === name) {
      return setTitleExpanded(undefined);
    }
    setTitleExpanded(name);
  };

  return (
    <div className="group mx-4 my-6 border-b-[2px] border-b-[#F1F1F1] first-of-type:mt-0 last-of-type:border-b-0">
      <div className="flex justify-between items-center mb-6">
        <Link
          aria-controls={`nav-expanded-content-${name}`}
          aria-expanded={expanded}
          href={ifLinks ? '#0' : (url as string)}
          onClick={toggleCategoryDisplay}
          className="text-[20px] font-medium text-[#1d252c] no-underline flex justify-between items-center w-full"
        >
          {name || title}
          {ifLinks && (
            <div className="p-0">
              <Image
                alt="Expand navigation list"
                src={icChevronDown}
                className={cx(
                  'transition-transform duration-300 ease-[cubic-bezier(0,0.7,0.3,1)]',
                  { 'rotate-180': expanded, 'rotate-0': !expanded }
                )}
              />
            </div>
          )}
        </Link>
      </div>
      <div 
        className={cx('flex flex-col flex-wrap', { 'flex': expanded, 'hidden': !expanded })}
        id={`nav-expanded-content-${name}`}
      >
        {isMeganav ? (
          <>
            <ul
              data-testid="meganav-links"
              className={cx(
                'm-0 w-full list-none py-4 tiny:hidden tablet:inline-block',
                {
                  'tablet:columns-1': tabletLinks
                    ? tabletLinks?.length < 5
                    : false,
                  'tablet:columns-2': tabletLinks
                    ? tabletLinks?.length >= 5 && tabletLinks?.length < 8
                    : false,
                  'tablet:columns-4': tabletLinks
                    ? tabletLinks?.length > 8
                    : false
                }
              )}
            >
              {tabletLinks &&
                tabletLinks.map(
                  (
                    {
                      fields: {
                        title,
                        url,
                        subcategoryLabel,
                        isNew,
                        isNewLabel
                      },
                      sys
                    }: any,
                    idx: number
                  ) => (
                    <li
                      className="mb-3"
                      key={`tablet-${sys?.id || title}-${idx}`}
                    >
                      {subcategoryLabel && (
                        <p className="!typography-title-4 mb-3 leading-6">
                          {tabletLinks[idx - 1]?.fields?.subcategoryLabel !==
                          subcategoryLabel
                            ? subcategoryLabel
                            : null}
                        </p>
                      )}
                      {url !== '#' ? (
                        <Link
                          href={url}
                          className={cx(
                            'text-[14px] font-normal text-[#1d252c] no-underline mt-0',
                            { '!mb-0 flex items-center gap-1': isNew }
                          )}
                        >
                          {title}
                          <IsNewBadge isNew={isNew} isNewLabel={isNewLabel} />
                        </Link>
                      ) : (
                        <div className="tablet:mb-36"></div>
                      )}
                    </li>
                  )
                )}
            </ul>
            <ul
              className={cx(
                'm-0 mb-[13px] inline-block w-full list-none group-first-of-type:-mb-[7px] tiny:inline-block tiny:columns-2 tablet:hidden',
                {
                  'tiny:columns-1': mobileLinks
                    ? mobileLinks?.length < 5
                    : false
                }
              )}
            >
              {mobileLinks &&
                mobileLinks.map(
                  (
                    {
                      fields: {
                        title,
                        url,
                        subcategoryLabel,
                        isNew,
                        isNewLabel
                      },
                      sys
                    }: any,
                    idx: number
                  ) => {
                    const nextLinkHasDifferentCategory =
                      mobileLinks[idx + 1]?.fields?.subcategoryLabel !==
                      subcategoryLabel;
                    const previousLinkHasDifferentCategory =
                      mobileLinks[idx - 1]?.fields?.subcategoryLabel !==
                      subcategoryLabel;
                    return (
                      <li
                        className={
                          nextLinkHasDifferentCategory ? 'mb-6' : 'mb-2'
                        }
                        key={`mobile-${sys?.id || title}-${idx}`}
                        data-testid={`mobile-link-${title ?? idx}`}
                      >
                        {previousLinkHasDifferentCategory && (
                          <p className="!typography-title-4 mb-2 leading-6">
                            {subcategoryLabel}
                          </p>
                        )}
                        {url !== '#' ? (
                          <Link
                            href={url}
                            className={cx(
                              'text-[14px] font-normal text-[#1d252c] no-underline mt-0',
                              { '!mb-0 !mt-0 flex items-center gap-1': isNew }
                            )}
                          >
                            {title}
                            <IsNewBadge isNew={isNew} isNewLabel={isNewLabel} />
                          </Link>
                        ) : (
                          <div className="tiny:mb-16"></div>
                        )}
                      </li>
                    );
                  }
                )}
            </ul>
          </>
        ) : (
          <ul className="w-full list-none m-0 p-4 p-0 columns-2">
            {links &&
              links.map(({ fields, sys }: ILink, idx: number) => (
                <li
                  key={`link-${sys?.id || fields?.title}-${idx}`}
                  data-testid="nav-link"
                  className="mb-6"
                >
                  <Link href={fields?.url} className="text-[14px] font-normal text-[#1d252c] no-underline mt-0">
                    {fields?.title}
                  </Link>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}
